/**
 * 访客星球地图对外的几样依赖：地图配置（GET /map/config，公开）、高德 JS 加载、WebGL 探测。
 * 默认就是 world_map 的 fetchMapConfig、loadAmap 和一次性的 WebGL 探测；用例、演示台用 MapDepsProvider 换掉（照 usePublicMap 的写法）。
 */
import { createContext, useContext, type ReactNode } from "react";
import { loadAmap, type AMapNamespace } from "@/features/world_map/amapLoader";
import { fetchMapConfig, type MapConfigState } from "@/features/world_map/mapConfig";

export interface MapDeps {
  loadConfig: (signal?: AbortSignal) => Promise<MapConfigState>;
  loadMap: (config: MapConfigState) => Promise<AMapNamespace>;
  /** 这台设备能不能建 WebGL（高德 JS API 2.0、3D 星球都要） */
  webgl: () => boolean;
}

/** 建一个 WebGL 上下文试试，建得起来就马上放掉。 */
export function probeWebGL(): boolean {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2") ?? canvas.getContext("webgl");
    if (!gl) return false;
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return true;
  } catch {
    return false;
  }
}

const DEFAULT_DEPS: MapDeps = { loadConfig: fetchMapConfig, loadMap: loadAmap, webgl: probeWebGL };
const DepsContext = createContext<MapDeps>(DEFAULT_DEPS);

export function MapDepsProvider({ deps, children }: { deps: Partial<MapDeps>; children: ReactNode }) {
  return <DepsContext.Provider value={{ ...DEFAULT_DEPS, ...deps }}>{children}</DepsContext.Provider>;
}

export function useMapDeps(): MapDeps {
  return useContext(DepsContext);
}

/** 访客地图的镜头记在 world_map/mapSession 的 "public" 作用域（不和玩家地图的镜头混）。 */
export const PUBLIC_MAP_SCOPE = "public";
